import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";

export const fmtDate = (s, f = "MMM d, yyyy") => {
  try { return format(new Date(s), f); } catch { return s; }
};
export const fmtDateTime = (s) => fmtDate(s, "MMM d, yyyy · h:mm a");

// days until the given date: 0 = within the next 24h, -1 = past
export const daysLeft = (s) => {
  try {
    const h = (new Date(s).getTime() - Date.now()) / 3600000;
    if (h < 0) return -1;
    return h <= 24 ? 0 : Math.round(h / 24);
  } catch { return null; }
};
export const daysLeftLabel = (s) => {
  const d = daysLeft(s);
  if (d === null || d < 0) return null;
  return d === 0 ? "Closes today" : d === 1 ? "1 day left" : `${d} days left`;
};

export const revealV = {
  hidden: { opacity: 0, y: 34 },
  show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.2, 0.8, 0.2, 1] } },
};

export function Reveal({ children, className, delay = 0, style }) {
  return (
    <motion.div
      className={className}
      style={style}
      variants={revealV}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      transition={{ delay }}
    >
      {children}
    </motion.div>
  );
}

export function useCountdown(dateStr) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const target = new Date(dateStr).getTime();
    const tick = () => setLeft(Math.max(0, target - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [dateStr]);
  const pad = (n) => String(n).padStart(2, "0");
  return {
    d: pad(Math.floor(left / 86400000)),
    h: pad(Math.floor((left % 86400000) / 3600000)),
    m: pad(Math.floor((left % 3600000) / 60000)),
    s: pad(Math.floor((left % 60000) / 1000)),
  };
}

export function Countdown({ date, seconds = false, className, label }) {
  const c = useCountdown(date);
  return (
    <span className={className}>
      {label && <em className="count-label">{label}</em>}
      <b>{c.d}</b>d <b>{c.h}</b>h <b>{c.m}</b>m{seconds ? <> <b>{c.s}</b>s</> : null}
    </span>
  );
}

export function useScrolledNav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", on, { passive: true });
    on();
    return () => window.removeEventListener("scroll", on);
  }, []);
  return scrolled;
}

export function Lightbox({ src, caption, onClose }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  if (!src) return null;
  return (
    <div className="lightbox" onClick={onClose} data-testid="lightbox">
      <img src={src} alt={caption || ""} onClick={(e) => e.stopPropagation()} />
      {caption && <div className="lb-cap" data-testid="lightbox-caption">{caption}</div>}
      <button className="lb-close" aria-label="Close">✕</button>
    </div>
  );
}
